'use client';

import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import {
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Badge } from '@/components/shadcn/ui/badge';
import { buttonVariants } from '@/components/shadcn/ui/button';
import { Input } from '@/components/shadcn/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import { ScrollArea } from '@/components/shadcn/ui/scroll-area';
import { cn } from '@/lib/cn';

// Types
export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxTagInputProps {
  value: string[];
  onChange: (values: string[]) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
}

export function ComboboxTagInput({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  className,
  disabled = false,
}: ComboboxTagInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>();

  // Trigger 너비 측정
  useEffect(() => {
    if (!triggerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      setTriggerWidth(entries[0].contentRect.width);
    });

    resizeObserver.observe(triggerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Popover 열릴 때 검색 입력에 포커스
  useEffect(() => {
    if (open && inputRef.current) {
      // 약간의 딜레이 후 포커스 (애니메이션 완료 후)
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // 검색 필터링된 옵션
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const searchLower = search.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchLower),
    );
  }, [options, search]);

  // value를 label로 변환하기 위한 Map
  const optionMap = useMemo(
    () => new Map(options.map((opt) => [opt.value, opt.label])),
    [options],
  );

  const handleSelect = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleRemove = (optionValue: string, e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionValue));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // ESC로 팝오버 닫기
    if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'h-auto min-h-9 w-full justify-between p-2',
            disabled && 'cursor-not-allowed opacity-50',
            className,
          )}
        >
          <div className="flex flex-1 flex-wrap items-center gap-1">
            {value.length > 0 ? (
              value.map((v) => (
                <Badge
                  key={v}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  <span>{optionMap.get(v) ?? v}</span>
                  {!disabled && (
                    <div
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer rounded-sm hover:bg-gray-200"
                      onClick={(e) => handleRemove(v, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRemove(v, e as unknown as MouseEvent);
                        }
                      }}
                    >
                      <XIcon className="size-3" />
                    </div>
                  )}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: triggerWidth }}
        align="start"
      >
        <div className="flex flex-col">
          {/* 검색 입력 */}
          <div className="border-b p-2">
            <Input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              className="h-8"
            />
          </div>

          {/* 옵션 목록 */}
          <ScrollArea className="max-h-[200px]">
            {filteredOptions.length === 0 ? (
              <div className="text-muted-foreground p-4 text-center text-sm">
                {emptyMessage}
              </div>
            ) : (
              <div className="p-1">
                {filteredOptions.map((option) => {
                  const isSelected = value.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelect(option.value)}
                      className={cn(
                        'flex w-full cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm outline-none',
                        'hover:bg-accent hover:text-accent-foreground',
                        isSelected && 'bg-accent/50',
                      )}
                    >
                      <span>{option.label}</span>
                      {isSelected && <CheckIcon className="size-4" />}
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
}
